import { useEffect, useRef, useState } from "react";

/**
 * HeroVideoMontage — cinematic multi-scene looping background.
 *
 * Two overlapping <video> elements (A + B) pre-load consecutive scenes and
 * crossfade between them every `sceneDuration` seconds. Mute / autoplay /
 * playsInline / no-controls so it behaves as a background.
 *
 * Props:
 *   scenes: [{ src, poster? }]  ordered list of clips to cycle through
 *   sceneDuration: seconds each scene stays on-screen before crossfade (default 3.6)
 *   fadeDuration: crossfade in ms (default 650)
 *   poster: fallback image shown before any video loads
 */
export const HeroVideoMontage = ({
  scenes,
  sceneDuration = 3.6,
  fadeDuration = 650,
  poster,
  className = "",
}) => {
  const [active, setActive] = useState("A"); // which buffer is foreground
  const [indexes, setIndexes] = useState({ A: 0, B: 1 }); // scene index per buffer
  const videoARef = useRef(null);
  const videoBRef = useRef(null);
  const sceneIndex = useRef(0); // last scene started

  useEffect(() => {
    if (!scenes?.length) return;
    const vA = videoARef.current;
    const vB = videoBRef.current;
    if (!vA || !vB) return;

    // Kick off playback on A
    const safePlay = (v) => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    safePlay(vA);

    const interval = setInterval(() => {
      sceneIndex.current = (sceneIndex.current + 1) % scenes.length;
      const nextIdx = sceneIndex.current;
      // The currently-hidden buffer becomes the new active one. Its src is
      // already one ahead, so we just advance the indexes and swap active.
      setActive((prev) => {
        const incoming = prev === "A" ? "B" : "A";
        const outgoing = prev;
        // Start the incoming video (it's already preloaded to the right scene)
        const inEl = incoming === "A" ? videoARef.current : videoBRef.current;
        const outEl = outgoing === "A" ? videoARef.current : videoBRef.current;
        if (inEl) {
          try { inEl.currentTime = 0; } catch (e) { /* ignore */ }
          safePlay(inEl);
        }
        // After the crossfade, pause the outgoing and move it to the scene
        // that comes after the next (so it's ready for the next switch).
        window.setTimeout(() => {
          if (outEl) {
            try { outEl.pause(); } catch (e) { /* ignore */ }
          }
          const prepIdx = (nextIdx + 1) % scenes.length;
          setIndexes((ix) => ({
            ...ix,
            [outgoing]: prepIdx,
          }));
        }, fadeDuration + 80);
        return incoming;
      });
    }, sceneDuration * 1000);

    return () => clearInterval(interval);
  }, [scenes, sceneDuration, fadeDuration]);

  if (!scenes?.length) return null;

  const aSrc = scenes[indexes.A]?.src;
  const bSrc = scenes[indexes.B]?.src;

  return (
    <div className={`absolute inset-0 ${className}`}>
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <video
        ref={videoARef}
        src={aSrc}
        autoPlay
        muted
        playsInline
        preload="auto"
        loop={false}
        poster={poster}
        aria-hidden="true"
        data-testid="hero-video"
        data-buffer="A"
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          opacity: active === "A" ? 1 : 0,
          transition: `opacity ${fadeDuration}ms ease-in-out`,
        }}
      />
      <video
        ref={videoBRef}
        src={bSrc}
        muted
        playsInline
        preload="auto"
        loop={false}
        poster={poster}
        aria-hidden="true"
        data-buffer="B"
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          opacity: active === "B" ? 1 : 0,
          transition: `opacity ${fadeDuration}ms ease-in-out`,
        }}
      />
    </div>
  );
};

export default HeroVideoMontage;
